import React from 'react';

import { XButton } from '@hero/ui-kit/inputs/XButton';
import P from '@hero/ui-kit/typography/P';
import Filter from '../../../../components/Filter';
import useAssignCareManagers from '../../api/useAssignCareManagers';
import useGetCareManagers from '../../api/useGetCareManagers';
import CareManagerDropdown from '../../components/CareManagerDropdown';
import * as Style from './style.module.scss';

interface BulkActionsProps {
    selectedMemberIds: Set<number>;
    totalCount?: number;
    onCancel: () => void;
    onSuccess: () => void;
}

const BulkActions: React.FC<BulkActionsProps> = ({
    selectedMemberIds,
    totalCount,
    onCancel,
    onSuccess
}) => {
    const { data: careManagers } = useGetCareManagers();
    const { mutateAsync, isPending } = useAssignCareManagers(onSuccess);

    const handleAssign = React.useCallback(
        (careManagerId: number) => {
            if (selectedMemberIds.size === 0) {
                return;
            }

            mutateAsync({
                payload: {
                    care_manager_ids: [careManagerId],
                    member_ids: Array.from(selectedMemberIds)
                }
            });
        },
        [selectedMemberIds, mutateAsync]
    );

    const selectedCount = selectedMemberIds.size;
    const noCareManagers = !careManagers?.length;
    const isDisabled = selectedCount === 0 || noCareManagers;

    const countLabel = totalCount
        ? `${selectedCount} of ${totalCount} ${totalCount === 1 ? 'member' : 'members'} selected`
        : `${selectedCount} ${selectedCount === 1 ? 'member' : 'members'} selected`;

    return (
        <div className={Style.floatingWrapper}>
            <div className={Style.content}>
                <P noDefaultMargin className={Style.selectedCount}>
                    {countLabel}
                </P>

                <Filter
                    filterLabel={isPending ? 'Assigning...' : 'Assign Care Manager'}
                    width="28rem"
                    className={Style.filterDropUp}
                    disabled={isDisabled || isPending}
                >
                    <CareManagerDropdown
                        careManagers={careManagers}
                        onSelect={(cm) => handleAssign(cm.id)}
                        disabled={isDisabled || isPending}
                    />
                </Filter>
            </div>

            <XButton
                alt="Cancel"
                onClick={onCancel}
                styles={{ lineHeight: 0 }}
                className={Style.xIcon}
            />
        </div>
    );
};

export default BulkActions;
